<script>
  import { onDestroy, onMount } from "svelte";

  import { v4 } from "uuid";

  import { DoubleBounce } from "svelte-loading-spinners";

  import { apiMethods } from "../../Services/index.svelte";

  import { downLoadIcon, deleteIcon } from "../../Utils/utilConstants.svelte";

  import { PdfTemplateTypes } from "../../Utils/utilEnums.svelte";

  import {
    getFormatDateAndTime,
    handleDownloadPDF,
    showError,
    showInfo,
  } from "../../Utils/utilFunctions.svelte";

  import { PageLoading, NotificationModal } from "./index.svelte";

  export let relId,
    doctorId,
    allFiles = [],
    title = "Files";

  let isLoading = false,
    isUploading = false,
    fileInput,
    selectedFile = {},
    isDeleting = false,
    isDeleteFileModalOpen = false;

  const dragNDropId = v4();

  onMount(() => {
    enableFileDragAndDrop();
  });

  onDestroy(() => {
    disableFileDragAndDrop();
  });

  const loadFiles = async () => {
    if (!relId) return (allFiles = []);

    isLoading = true;

    await apiMethods.getMethod
      .getEventById(relId)
      .then((res) => {
        if (!res || res.error) {
          showError(
            res.error ??
              "Failed to load encounter notes, please refresh the page and try again and contact support if the issue persists.",
          );
        } else allFiles = [...res.eventFiles];
      })
      .catch((err) => {
        showError(err);
      });

    isLoading = false;
  };

  const onFileChange = async (e, isDropFile = false) => {
    const files = isDropFile ? e.dataTransfer.files : e.target.files;
    const uploadPromises = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = file?.name ?? `file${i}`;

      const promise = new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          const fileObj = {
            doctorId,
            type: PdfTemplateTypes.EventAttachedFile,
            entityId: relId,
            fileName: fileName,
            uploadedFileBase64: e.target.result.split(",")[1],
          };
          resolve(fileObj);
        };
        reader.onerror = (err) => reject(err);
      });
      uploadPromises.push(promise);
    }

    try {
      const fileObjs = await Promise.all(uploadPromises);

      await Promise.all(fileObjs.map(uploadFile));

      loadFiles();
    } catch (err) {
      showError(
        "Failed to upload files, please refresh the page and try again and contact support if the issue persists.",
      );
    }
  };

  const uploadFile = async (file) => {
    isUploading = true;

    await apiMethods.postMethod
      .uploadFile(file)
      .then((res) => {
        if (res.error) throw res.error;
      })
      .catch((err) => {
        showError(
          "Failed to upload file, please refresh the page and try again later.",
        );
      });

    isUploading = false;
  };

  const deleteFile = async () => {
    isDeleting = true;

    await apiMethods.deleteMethod
      .deleteFileById(selectedFile.id)
      .then((res) => {
        if (!res || res.error) throw res.error;
        else {
          isDeleting = false;
          showInfo("File Deleted");
          _closeDeleteModal();
          loadFiles();
        }
      })
      .catch((err) => {
        isDeleting = false;
        _closeDeleteModal();
        showError(
          err ??
            "Failed to delete file, please refresh the page and try again and contact support if the issue persists.",
        );
      });
  };

  const _openDeleteModal = (file) => {
    selectedFile = file;
    isDeleteFileModalOpen = true;
  };

  const _closeDeleteModal = () => {
    selectedFile = {};
    isDeleteFileModalOpen = false;
  };

  const downloadFile = (file) => {
    if (!file.id) return;
    file.isDownloading = true;
    allFiles = [...allFiles];
    apiMethods.getMethod
      .getFileById(file.id)
      .then((res) => {
        file.isDownloading = false;
        allFiles = [...allFiles];
        if (res.error) throw res.error;
        handleDownloadPDF(
          res.fileContent.fileContents,
          res.fileContent.fileDownloadName,
          true,
        );
      })
      .catch((err) => {
        console.log(err);
        showError("Failed to download file.");
        file.isDownloading = false;
        allFiles = [...allFiles];
      });
  };

  const enableFileDragAndDrop = () => {
    const enableDropArea = document.getElementById(dragNDropId);

    if (!enableDropArea) return;

    const { handleDragOver, handleDragLeave, handleDrop } =
      createDragAndDropHandler(enableDropArea);

    enableDropArea.addEventListener("dragover", handleDragOver);
    enableDropArea.addEventListener("dragleave", handleDragLeave);
    enableDropArea.addEventListener("drop", handleDrop);
  };

  const disableFileDragAndDrop = () => {
    const enableDropArea = document.getElementById(dragNDropId);

    if (!enableDropArea) return;

    const { handleDragOver, handleDragLeave, handleDrop } =
      createDragAndDropHandler(enableDropArea);

    enableDropArea.removeEventListener("dragover", handleDragOver);
    enableDropArea.removeEventListener("dragleave", handleDragLeave);
    enableDropArea.removeEventListener("drop", handleDrop);
  };

  const createDragAndDropHandler = (dropArea) => {
    const handleDragOver = (e) => {
      e.preventDefault();
      dropArea.classList.add(
        "outline-dashed",
        "outline-secondary",
        "outline-2",
      );
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      dropArea.classList.remove(
        "outline-dashed",
        "outline-secondary",
        "outline-2",
      );
    };

    const handleDrop = (e) => {
      e.preventDefault();

      if (isUploading) return;

      onFileChange(e, true);

      dropArea.classList.remove(
        "outline-dashed",
        "outline-secondary",
        "outline-2",
      );
    };

    return { handleDragOver, handleDragLeave, handleDrop };
  };
</script>

<div class="flex flex-col gap-4 flex-1 p-2" id={dragNDropId}>
  <input
    type="file"
    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
    style="display: none"
    id="fileInput"
    on:change={onFileChange}
    bind:this={fileInput}
    multiple
  />
  <div class="flex flex-row justify-between items-end">
    <p class="font-medium">{title}</p>
    <button
      class="btn btn-ghost btn-sm normal-case"
      on:click|preventDefault={() => fileInput.click()}
      disabled={isLoading || isUploading}
    >
      {#if isLoading || isUploading}
        <DoubleBounce size="12" color="#FF3E00" unit="px" duration="1s" />
      {:else}
        Upload File
      {/if}
    </button>
  </div>
  <div>
    {#if isLoading}
      <PageLoading />
    {:else if !allFiles.length}
      <p>No files available</p>
    {:else}
      <div class="grid grid-cols-6 gap-x-3 gap-y-1 px-2 w-full">
        {#each allFiles as file}
          <span class="col-span-3">{file.fileName}</span>
          <span class="col-span-2"
            >{getFormatDateAndTime(file.createdDate)}</span
          >
          <div class="col-span-1 flex flex-row justify-end">
            <button
              class="btn btn-sm btn-ghost btn-circle"
              disabled={file.isDownloading}
              on:click={() => downloadFile(file)}
            >
              {#if file.isDownloading}
                <DoubleBounce
                  size="12"
                  color="#FF3E00"
                  unit="px"
                  duration="1s"
                />
              {:else}
                <i class={downLoadIcon} />
              {/if}
            </button>
            <button
              class="btn btn-sm btn-ghost btn-circle"
              on:click={() => _openDeleteModal(file)}
            >
              <i class={deleteIcon} />
            </button>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<NotificationModal
  title="Delete File"
  isModalOpen={isDeleteFileModalOpen}
  isConfirming={isDeleting}
  cancelText="Delete"
  closeModal={deleteFile}
  submitText="Cancel"
  confirmAction={_closeDeleteModal}
  isReverseBtns
  closeModalFunction={_closeDeleteModal}
>
  Delete this File?
</NotificationModal>
